import { friseChantier } from '@imp/shared/frise-chantier';
import { messageErreur } from '@imp/shared/message-erreur';
import { colonnesActives } from '@imp/shared/suivi-chantiers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Camera,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  LocateFixed,
  MapPin,
  Navigation,
  Printer,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EVENEMENT_STATUT_LABELS } from '@/modules/agenda/api';
import { useAuth } from '@/modules/auth/AuthContext';
import { FriseChantier } from '@/modules/chantiers/FriseChantier';
import {
  CHANTIER_STATUT_LABELS,
  type ChantierDocument,
  type ChantierInput,
  type ChantierPhoto,
  createChantier,
  deleteChantier,
  deleteDocument,
  deletePhoto,
  deletePointage,
  DOCUMENT_TYPE_LABELS,
  employeNom,
  getChantier,
  getHistoriqueChantier,
  itineraireUrl,
  listDocuments,
  listEmployes,
  listEvenementsForChantier,
  listPhotos,
  listPointages,
  PHOTO_PHASE_LABELS,
  type Pointage,
  type PrepStatut,
  savePointage,
  updateChantier,
  uploadDocument,
  uploadPhoto,
} from '@/modules/chantiers/api';
import { listColonnesSuivi } from '@/modules/chantiers/suivi-api';
import { ChoixClient } from '@/modules/clients/ChoixClient';
import { listContacts } from '@/modules/clients/api';

interface FormState {
  libelle: string;
  description: string;
  contact_id: string;
  type_travaux: string;
  telephone: string;
  statut: string;
  chef_chantier_id: string;
  adresse_ligne1: string;
  adresse_ligne2: string;
  code_postal: string;
  ville: string;
  latitude: string;
  longitude: string;
  date_prevue_debut: string;
  date_prevue_fin: string;
  avancement: string;
  notes: string;
  montant_achats: string;
  montant_vente_ht: string;
  jours_realises: string;
  commercial_id: string;
  nacelle_requise: boolean;
  nacelle_magasin: string;
  nacelle_commandee: boolean;
  prep: Record<string, string>;
}

const EMPTY: FormState = {
  libelle: '',
  description: '',
  contact_id: '',
  type_travaux: '',
  telephone: '',
  statut: 'prospect',
  chef_chantier_id: '',
  adresse_ligne1: '',
  adresse_ligne2: '',
  code_postal: '',
  ville: '',
  latitude: '',
  longitude: '',
  date_prevue_debut: '',
  date_prevue_fin: '',
  avancement: '0',
  notes: '',
  montant_achats: '0',
  montant_vente_ht: '0',
  jours_realises: '0',
  commercial_id: '',
  nacelle_requise: false,
  nacelle_magasin: '',
  nacelle_commandee: false,
  prep: {},
};

function nn(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

function formatTaille(n: number | null): string {
  if (!n) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

export function ChantierFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profil } = useAuth();
  const isAdmin = profil?.isAdmin ?? false;

  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  // Colonnes de préparation, réglées à l'écran Suivi (repli sur celles d'origine).
  const colonnesQ = useQuery({ queryKey: ['suivi-colonnes'], queryFn: listColonnesSuivi });
  const colonnesPrep = colonnesActives(colonnesQ.data ?? []);
  const employesQ = useQuery({ queryKey: ['employes'], queryFn: listEmployes });
  const chantierQ = useQuery({
    queryKey: ['chantier', id],
    queryFn: () => getChantier(id ?? ''),
    enabled: isEdit,
  });
  const eventsQ = useQuery({
    queryKey: ['chantier-evenements', id],
    queryFn: () => listEvenementsForChantier(id ?? ''),
    enabled: isEdit,
  });
  const photosQ = useQuery({
    queryKey: ['chantier-photos', id],
    queryFn: () => listPhotos(id ?? ''),
    enabled: isEdit,
  });
  const documentsQ = useQuery({
    queryKey: ['chantier-documents', id],
    queryFn: () => listDocuments(id ?? ''),
    enabled: isEdit,
  });
  // Devis et factures du chantier : la frise en tire les étapes commerciales.
  const historiqueQ = useQuery({
    queryKey: ['chantier-historique', id],
    queryFn: () => getHistoriqueChantier(id ?? ''),
    enabled: isEdit,
  });
  const pointagesQ = useQuery({
    queryKey: ['chantier-pointages', id],
    queryFn: () => listPointages(id ?? ''),
    enabled: isEdit,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [photoPhase, setPhotoPhase] = useState('pendant');
  const fileRef = useRef<HTMLInputElement>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [docType, setDocType] = useState('autre');
  const docFileRef = useRef<HTMLInputElement>(null);
  const [pdfPreview, setPdfPreview] = useState<ChantierDocument | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pt, setPt] = useState(() => ({
    employe_id: '',
    jour: new Date().toISOString().slice(0, 10),
    hn: '',
    h25: '',
    h50: '',
    paniers: '',
    km: '',
    notes: '',
  }));
  const setPtF = (patch: Partial<typeof pt>) => {
    setPt((p) => ({ ...p, ...patch }));
  };
  const set = (patch: Partial<FormState>) => {
    setForm((f) => ({ ...f, ...patch }));
  };

  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const captureGps = () => {
    if (!('geolocation' in navigator)) {
      setGeoError('Géolocalisation non disponible sur cet appareil.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set({
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        });
        setGeoLoading(false);
      },
      (err) => {
        setGeoError(err.message || 'Position indisponible (autorisation refusée ?).');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  useEffect(() => {
    const c = chantierQ.data;
    if (!c) return;
    setForm({
      libelle: c.libelle,
      description: c.description ?? '',
      contact_id: c.contact_id ?? '',
      type_travaux: c.type_travaux ?? '',
      telephone: c.metadata?.telephone ?? '',
      statut: c.statut,
      chef_chantier_id: c.chef_chantier_id ?? '',
      adresse_ligne1: c.adresse_ligne1 ?? '',
      adresse_ligne2: c.adresse_ligne2 ?? '',
      code_postal: c.code_postal ?? '',
      ville: c.ville ?? '',
      latitude: c.latitude ?? '',
      longitude: c.longitude ?? '',
      date_prevue_debut: c.date_prevue_debut ?? '',
      date_prevue_fin: c.date_prevue_fin ?? '',
      avancement: String(c.avancement_pourcentage),
      notes: c.notes ?? '',
      montant_achats: String(c.metadata?.montant_achats ?? 0),
      montant_vente_ht: String(c.metadata?.montant_vente_ht ?? 0),
      jours_realises: String(c.metadata?.jours_realises ?? 0),
      commercial_id: c.metadata?.commercial_id ?? '',
      nacelle_requise: c.metadata?.nacelle_requise ?? false,
      nacelle_magasin: c.metadata?.nacelle_magasin ?? '',
      nacelle_commandee: c.metadata?.nacelle_commandee ?? false,
      // Une clé absente vaut « non concerné » : on ne garde que ce qui a une valeur.
      prep: Object.fromEntries(
        Object.entries(c.metadata?.prep ?? {}).filter(([, v]) => typeof v === 'string'),
      ) as Record<string, string>,
    });
  }, [chantierQ.data]);

  // Aperçu PDF : on télécharge le fichier en blob (l'en-tête X-Frame-Options du
  // Storage interdit l'iframe direct) puis on l'affiche via une URL locale.
  useEffect(() => {
    const url = pdfPreview?.url;
    if (!url) {
      setPdfBlobUrl(null);
      return;
    }
    let active = true;
    let obj: string | null = null;
    setPdfBlobUrl(null);
    void fetch(url)
      .then((r) => r.blob())
      .then((b) => {
        if (!active) return;
        obj = URL.createObjectURL(b);
        setPdfBlobUrl(obj);
      })
      .catch(() => {
        /* aperçu indisponible : le bouton « Onglet » reste utilisable */
      });
    return () => {
      active = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [pdfPreview]);

  const toInput = (s: FormState): ChantierInput => ({
    libelle: s.libelle.trim(),
    description: nn(s.description),
    contact_id: s.contact_id === '' ? null : s.contact_id,
    type_travaux: nn(s.type_travaux),
    statut: s.statut,
    chef_chantier_id: s.chef_chantier_id === '' ? null : s.chef_chantier_id,
    adresse_ligne1: nn(s.adresse_ligne1),
    adresse_ligne2: nn(s.adresse_ligne2),
    code_postal: nn(s.code_postal),
    ville: nn(s.ville),
    latitude: nn(s.latitude),
    longitude: nn(s.longitude),
    date_prevue_debut: s.date_prevue_debut === '' ? null : s.date_prevue_debut,
    date_prevue_fin: s.date_prevue_fin === '' ? null : s.date_prevue_fin,
    avancement_pourcentage: Math.max(0, Math.min(100, Number(s.avancement) || 0)),
    notes: nn(s.notes),
    metadata: {
      ...(s.telephone.trim() === '' ? {} : { telephone: s.telephone.trim() }),
      montant_achats: Number(s.montant_achats.replace(',', '.')) || 0,
      montant_vente_ht: Number(s.montant_vente_ht.replace(',', '.')) || 0,
      jours_realises: Number(s.jours_realises) || 0,
      ...(s.commercial_id === '' ? {} : { commercial_id: s.commercial_id }),
      ...(s.nacelle_requise
        ? {
            nacelle_requise: true,
            nacelle_commandee: s.nacelle_commandee,
            ...(s.nacelle_magasin.trim() === ''
              ? {}
              : { nacelle_magasin: s.nacelle_magasin.trim() }),
          }
        : {}),
      // Les colonnes sont configurables : on réécrit ce qui a été coché sans
      // présumer des clés, et sans perdre celles d'une colonne masquée.
      prep: Object.fromEntries(Object.entries(s.prep).map(([cle, v]) => [cle, v as PrepStatut])),
    },
  });

  const mutation = useMutation({
    mutationFn: async (s: FormState) => {
      if (isEdit && id) {
        await updateChantier(id, toInput(s));
        return id;
      }
      return createChantier(toInput(s));
    },
    onSuccess: (chantierId) => {
      void queryClient.invalidateQueries({ queryKey: ['chantiers'] });
      void queryClient.invalidateQueries({ queryKey: ['chantier', chantierId] });
      navigate(`/chantiers/${chantierId}`);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteChantier(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantiers'] });
      navigate('/chantiers');
    },
  });

  const uploadMut = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadPhoto(id ?? '', f, photoPhase);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantier-photos', id] });
    },
  });
  const deletePhotoMut = useMutation({
    mutationFn: (p: ChantierPhoto) => deletePhoto(p),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantier-photos', id] });
    },
  });
  const uploadDocMut = useMutation({
    mutationFn: (file: File) => uploadDocument(id ?? '', file, docType),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantier-documents', id] });
    },
  });
  const deleteDocMut = useMutation({
    mutationFn: (d: ChantierDocument) => deleteDocument(d),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantier-documents', id] });
    },
  });
  const savePtMut = useMutation({
    mutationFn: () =>
      savePointage(id ?? '', {
        employe_id: pt.employe_id,
        jour: pt.jour,
        heures_normales: pt.hn,
        heures_sup_25: pt.h25,
        heures_sup_50: pt.h50,
        paniers_repas: pt.paniers,
        km: pt.km,
        notes: pt.notes,
      }),
    onSuccess: () => {
      setPt((p) => ({
        ...p,
        employe_id: '',
        hn: '',
        h25: '',
        h50: '',
        paniers: '',
        km: '',
        notes: '',
      }));
      void queryClient.invalidateQueries({ queryKey: ['chantier-pointages', id] });
    },
  });
  const deletePtMut = useMutation({
    mutationFn: (ptId: string) => deletePointage(ptId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chantier-pointages', id] });
    },
  });

  const canSave = form.libelle.trim() !== '';
  const itineraire = itineraireUrl(form);
  const photos = photosQ.data ?? [];
  const current = lightboxIdx !== null ? photos[lightboxIdx] : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/chantiers" aria-label="Retour">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {isEdit ? form.libelle || 'Chantier' : 'Nouveau chantier'}
            </h1>
            <span className="text-muted-foreground text-sm">
              {isEdit && chantierQ.data?.numero ? (
                <span className="font-mono">{chantierQ.data.numero}</span>
              ) : null}
              {isEdit && chantierQ.data?.numero ? ' · ' : ''}
              {CHANTIER_STATUT_LABELS[form.statut] ?? form.statut}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* La fiche n'existe que pour un chantier enregistré : sur un
              nouveau, elle n'aurait ni numéro ni client à imprimer. */}
          {isEdit ? (
            <Button asChild variant="outline">
              <Link to={`/chantiers/${id ?? ''}/fiche`}>
                <Printer className="h-4 w-4" /> Fiche de chantier
              </Link>
            </Button>
          ) : null}
          {itineraire ? (
            <Button asChild variant="outline">
              <a href={itineraire} target="_blank" rel="noreferrer">
                <Navigation className="h-4 w-4" /> S'y rendre
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Toute l'histoire du chantier, et la prochaine action, en un coup d'œil. */}
      {isEdit ? (
        <FriseChantier
          indisponible={historiqueQ.isError || eventsQ.isError || chantierQ.isError}
          frise={
            historiqueQ.data && eventsQ.data && chantierQ.data
              ? friseChantier({
                  chantierStatut: chantierQ.data.statut,
                  devis: historiqueQ.data.devis,
                  factures: historiqueQ.data.factures,
                  interventions: eventsQ.data.map((e) => ({
                    debutAt: e.debut_at,
                    finAt: e.fin_at,
                    statut: e.statut,
                  })),
                  maintenant: new Date().toISOString(),
                })
              : null
          }
        />
      ) : null}

      {mutation.isError ? (
        <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border px-4 py-2 text-sm">
          Échec : {messageErreur(mutation.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Informations</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="libelle">Libellé *</Label>
            <Input
              id="libelle"
              value={form.libelle}
              onChange={(e) => {
                set({ libelle: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_id">Client</Label>
            <ChoixClient
              id="contact_id"
              clients={contactsQ.data ?? []}
              chargement={contactsQ.isLoading}
              value={form.contact_id}
              labelAucun="— Aucun (à définir plus tard) —"
              onChange={(v) => {
                set({ contact_id: v });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="type_travaux">Type de travaux</Label>
            <Input
              id="type_travaux"
              placeholder="Réfection, isolation…"
              value={form.type_travaux}
              onChange={(e) => {
                set({ type_travaux: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input
              id="telephone"
              type="tel"
              inputMode="tel"
              placeholder="06 12 34 56 78"
              value={form.telephone}
              onChange={(e) => {
                set({ telephone: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="statut">Statut</Label>
            <Select
              id="statut"
              value={form.statut}
              onChange={(e) => {
                set({ statut: e.target.value });
              }}
            >
              {Object.entries(CHANTIER_STATUT_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chef">Chef de chantier</Label>
            <Select
              id="chef"
              value={form.chef_chantier_id}
              onChange={(e) => {
                set({ chef_chantier_id: e.target.value });
              }}
            >
              <option value="">— Non assigné —</option>
              {(employesQ.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {employeNom(emp)}
                  {emp.poste ? ` (${emp.poste})` : ''}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Localisation
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="adresse_ligne1">Adresse</Label>
            <Input
              id="adresse_ligne1"
              placeholder="12 route de Banon"
              value={form.adresse_ligne1}
              onChange={(e) => {
                set({ adresse_ligne1: e.target.value });
              }}
            />
            <p className="text-muted-foreground text-xs">
              L&apos;adresse des travaux, qui s&apos;imprime sur le devis. Laissez vide si le
              chantier est chez le client : c&apos;est alors son adresse qui sera reprise.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="code_postal">Code postal</Label>
            <Input
              id="code_postal"
              value={form.code_postal}
              onChange={(e) => {
                set({ code_postal: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ville">Ville</Label>
            <Input
              id="ville"
              value={form.ville}
              onChange={(e) => {
                set({ ville: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="latitude">Latitude GPS</Label>
            <Input
              id="latitude"
              placeholder="43.9601"
              value={form.latitude}
              onChange={(e) => {
                set({ latitude: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="longitude">Longitude GPS</Label>
            <Input
              id="longitude"
              placeholder="5.7783"
              value={form.longitude}
              onChange={(e) => {
                set({ longitude: e.target.value });
              }}
            />
          </div>
          <div className="sm:col-span-2">
            <Button type="button" variant="outline" disabled={geoLoading} onClick={captureGps}>
              <LocateFixed className="h-4 w-4" />
              {geoLoading ? 'Localisation…' : 'Récupérer ma position (sur place)'}
            </Button>
            {geoError ? <span className="text-destructive ml-2 text-xs">{geoError}</span> : null}
          </div>
          <p className="text-muted-foreground text-xs sm:col-span-2">
            Sur le chantier, appuyez sur « Récupérer ma position » pour enregistrer les coordonnées
            GPS exactes — le bouton « S'y rendre » ouvrira ensuite l'itinéraire pour les équipes.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Planning</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="date_prevue_debut">Début prévu</Label>
            <Input
              id="date_prevue_debut"
              type="date"
              value={form.date_prevue_debut}
              onChange={(e) => {
                set({ date_prevue_debut: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="date_prevue_fin">Fin prévue</Label>
            <Input
              id="date_prevue_fin"
              type="date"
              value={form.date_prevue_fin}
              onChange={(e) => {
                set({ date_prevue_fin: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="avancement">Avancement (%)</Label>
            <Input
              id="avancement"
              inputMode="numeric"
              value={form.avancement}
              onChange={(e) => {
                set({ avancement: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-3">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => {
                set({ notes: e.target.value });
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rentabilité</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="montant_vente_ht">Montant du devis HT (€)</Label>
            <Input
              id="montant_vente_ht"
              inputMode="decimal"
              value={form.montant_vente_ht}
              onChange={(e) => {
                set({ montant_vente_ht: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="montant_achats">Achats fournisseurs (€)</Label>
            <Input
              id="montant_achats"
              inputMode="decimal"
              value={form.montant_achats}
              onChange={(e) => {
                set({ montant_achats: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="jours_realises">Jours réalisés</Label>
            <Input
              id="jours_realises"
              inputMode="numeric"
              value={form.jours_realises}
              onChange={(e) => {
                set({ jours_realises: e.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="commercial_id">Commercial</Label>
            <Select
              id="commercial_id"
              value={form.commercial_id}
              onChange={(e) => {
                set({ commercial_id: e.target.value });
              }}
            >
              <option value="">— Non assigné —</option>
              {(employesQ.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {employeNom(emp)}
                </option>
              ))}
            </Select>
          </div>
          <p className="text-muted-foreground text-xs sm:col-span-3">
            Le montant du devis HT sert à calculer la marge (page Pilotage) tant qu'aucune facture
            n'est émise ; ensuite ce sont les factures liées qui font foi. Saisissez les achats
            fournisseurs en fin de mois.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" /> Nacelle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="accent-primary h-4 w-4"
              checked={form.nacelle_requise}
              onChange={(e) => {
                set({ nacelle_requise: e.target.checked });
              }}
            />
            Nacelle nécessaire pour ce chantier
          </label>
          {form.nacelle_requise ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="nacelle_magasin">Magasin / loueur</Label>
                <Input
                  id="nacelle_magasin"
                  list="nacelle-magasins"
                  placeholder="LOXAM, Kiloutou…"
                  value={form.nacelle_magasin}
                  onChange={(e) => {
                    set({ nacelle_magasin: e.target.value });
                  }}
                />
                <datalist id="nacelle-magasins">
                  <option value="LOXAM" />
                  <option value="Kiloutou" />
                  <option value="Loxam Manosque" />
                  <option value="Loxam Forcalquier" />
                </datalist>
              </div>
              <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium">
                <input
                  type="checkbox"
                  className="accent-primary h-4 w-4"
                  checked={form.nacelle_commandee}
                  onChange={(e) => {
                    set({ nacelle_commandee: e.target.checked });
                  }}
                />
                Nacelle déjà commandée
              </label>
            </div>
          ) : null}
          <p className="text-muted-foreground text-xs">
            Si « nécessaire » et pas encore commandée, une alerte « Commander la nacelle » (avec le
            magasin) apparaîtra dans Alertes.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Préparation du chantier</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {colonnesPrep.map((item) => (
            <div key={item.cle} className="space-y-1.5">
              <Label htmlFor={`prep_${item.cle}`}>{item.label}</Label>
              <Select
                id={`prep_${item.cle}`}
                value={form.prep[item.cle] ?? 'na'}
                onChange={(e) => {
                  set({ prep: { ...form.prep, [item.cle]: e.target.value } });
                }}
              >
                <option value="na">Non concerné</option>
                <option value="todo">À faire</option>
                <option value="done">Fait</option>
              </Select>
            </div>
          ))}
          <p className="text-muted-foreground text-xs sm:col-span-2">
            Chaque étape « À faire » génère une alerte (page Alertes) tant qu'elle n'est pas marquée
            « Fait ». Les étapes se règlent dans <strong>Suivi chantiers</strong>, où l’on peut
            aussi en ajouter.
          </p>
        </CardContent>
      </Card>

      {isEdit ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Interventions planifiées</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/agenda">Ouvrir l'agenda</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {(eventsQ.data ?? []).length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Aucune intervention planifiée. Depuis l'Agenda, créez un événement et liez-le à ce
                chantier.
              </p>
            ) : (
              <div className="divide-y">
                {(eventsQ.data ?? []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium">{e.titre}</span>
                    <span className="text-muted-foreground">
                      {new Date(e.debut_at).toLocaleString('fr-FR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}{' '}
                      · {EVENEMENT_STATUT_LABELS[e.statut] ?? e.statut}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {isEdit ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-4 w-4" /> Photos du chantier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40">
                <Select
                  value={photoPhase}
                  onChange={(e) => {
                    setPhotoPhase(e.target.value);
                  }}
                >
                  {Object.entries(PHOTO_PHASE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length > 0) uploadMut.mutate(files);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploadMut.isPending}
                onClick={() => {
                  fileRef.current?.click();
                }}
              >
                <Camera className="h-4 w-4" />{' '}
                {uploadMut.isPending ? 'Envoi…' : 'Ajouter des photos'}
              </Button>
            </div>
            {uploadMut.isError ? (
              <p className="text-destructive text-sm">{messageErreur(uploadMut.error)}</p>
            ) : null}
            {photos.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Aucune photo. Les équipes peuvent en ajouter depuis leur téléphone (avant / pendant
                / après). Touchez une photo pour l'agrandir.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {photos.map((p, idx) => (
                  <div key={p.id} className="group relative overflow-hidden rounded-lg border">
                    <button
                      type="button"
                      className="block w-full"
                      aria-label="Agrandir la photo"
                      onClick={() => {
                        setLightboxIdx(idx);
                      }}
                    >
                      {p.url ? (
                        <img
                          src={p.url}
                          alt={p.legende ?? ''}
                          className="aspect-square w-full cursor-zoom-in object-cover"
                        />
                      ) : (
                        <div className="bg-muted aspect-square w-full" />
                      )}
                    </button>
                    <span className="pointer-events-none absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      {PHOTO_PHASE_LABELS[p.phase] ?? p.phase}
                    </span>
                    <button
                      type="button"
                      aria-label="Supprimer la photo"
                      onClick={() => {
                        deletePhotoMut.mutate(p);
                      }}
                      className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {isEdit ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Documents (PDF)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-48">
                <Select
                  value={docType}
                  onChange={(e) => {
                    setDocType(e.target.value);
                  }}
                >
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              </div>
              <input
                ref={docFileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadDocMut.mutate(f);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploadDocMut.isPending}
                onClick={() => {
                  docFileRef.current?.click();
                }}
              >
                <FileText className="h-4 w-4" />{' '}
                {uploadDocMut.isPending ? 'Envoi…' : 'Ajouter un PDF'}
              </Button>
            </div>
            {uploadDocMut.isError ? (
              <p className="text-destructive text-sm">
                {messageErreur(uploadDocMut.error)}
              </p>
            ) : null}
            {(documentsQ.data ?? []).length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Aucun document. Joignez vos PDF (devis fournisseur, plans, permis, fiches
                techniques…).
              </p>
            ) : (
              <ul className="divide-y rounded-md border">
                {(documentsQ.data ?? []).map((d) => (
                  <li key={d.id} className="flex items-center gap-3 px-3 py-2">
                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{d.nom}</div>
                      <div className="text-muted-foreground text-xs">
                        {DOCUMENT_TYPE_LABELS[d.type] ?? d.type}
                        {d.taille_octets ? ` · ${formatTaille(d.taille_octets)}` : ''}
                      </div>
                    </div>
                    {d.url ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPdfPreview(d);
                        }}
                        className="text-primary inline-flex shrink-0 items-center gap-1 text-sm hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> Aperçu
                      </button>
                    ) : null}
                    <button
                      type="button"
                      aria-label="Supprimer le document"
                      onClick={() => {
                        deleteDocMut.mutate(d);
                      }}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      {isEdit ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pointage des heures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="pt_emp">Intervenant</Label>
                <Select
                  id="pt_emp"
                  value={pt.employe_id}
                  onChange={(e) => {
                    setPtF({ employe_id: e.target.value });
                  }}
                >
                  <option value="">— Choisir —</option>
                  {(employesQ.data ?? []).map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {employeNom(emp)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pt_jour">Jour</Label>
                <Input
                  id="pt_jour"
                  type="date"
                  value={pt.jour}
                  onChange={(e) => {
                    setPtF({ jour: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pt_hn">Heures normales</Label>
                <Input
                  id="pt_hn"
                  inputMode="decimal"
                  placeholder="7"
                  value={pt.hn}
                  onChange={(e) => {
                    setPtF({ hn: e.target.value });
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="pt_h25">+25 %</Label>
                  <Input
                    id="pt_h25"
                    inputMode="decimal"
                    value={pt.h25}
                    onChange={(e) => {
                      setPtF({ h25: e.target.value });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pt_h50">+50 %</Label>
                  <Input
                    id="pt_h50"
                    inputMode="decimal"
                    value={pt.h50}
                    onChange={(e) => {
                      setPtF({ h50: e.target.value });
                    }}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pt_pan">Paniers repas</Label>
                <Input
                  id="pt_pan"
                  inputMode="numeric"
                  value={pt.paniers}
                  onChange={(e) => {
                    setPtF({ paniers: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pt_km">Trajet (km)</Label>
                <Input
                  id="pt_km"
                  inputMode="decimal"
                  value={pt.km}
                  onChange={(e) => {
                    setPtF({ km: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="pt_notes">Notes</Label>
                <Input
                  id="pt_notes"
                  value={pt.notes}
                  onChange={(e) => {
                    setPtF({ notes: e.target.value });
                  }}
                />
              </div>
              <Button
                type="button"
                disabled={pt.employe_id === '' || savePtMut.isPending}
                onClick={() => {
                  savePtMut.mutate();
                }}
              >
                {savePtMut.isPending ? 'Ajout…' : 'Ajouter / mettre à jour'}
              </Button>
            </div>
            {savePtMut.isError ? (
              <p className="text-destructive text-sm">{messageErreur(savePtMut.error)}</p>
            ) : null}

            {(pointagesQ.data ?? []).length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Aucune heure saisie. Les chefs de chantier peuvent renseigner les heures ici, jour
                par jour.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                      <th className="py-2 pr-2">Jour</th>
                      <th className="py-2 pr-2">Intervenant</th>
                      <th className="py-2 pr-2 text-right">Normales</th>
                      <th className="py-2 pr-2 text-right">+25 %</th>
                      <th className="py-2 pr-2 text-right">+50 %</th>
                      <th className="py-2 pr-2 text-right">Paniers</th>
                      <th className="py-2 pr-2 text-right">Km</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {(pointagesQ.data ?? []).map((p: Pointage) => (
                      <tr key={p.id} className="border-b">
                        <td className="py-1.5 pr-2 tabular-nums">
                          {new Date(p.jour).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="py-1.5 pr-2">{p.employe_nom}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{p.heures_normales}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{p.heures_sup_25}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{p.heures_sup_50}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{p.paniers_repas}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{p.km}</td>
                        <td className="py-1.5 text-right">
                          <button
                            type="button"
                            aria-label="Supprimer"
                            onClick={() => {
                              deletePtMut.mutate(p.id);
                            }}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {current ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/90"
          onClick={() => {
            setLightboxIdx(null);
          }}
        >
          <div className="flex items-center justify-between gap-3 p-3 text-sm text-white">
            <span>
              {PHOTO_PHASE_LABELS[current.phase] ?? current.phase}
              {current.legende ? ` · ${current.legende}` : ''}
              <span className="ml-2 text-white/60">
                {photos.indexOf(current) + 1}/{photos.length}
              </span>
            </span>
            <button
              type="button"
              aria-label="Fermer"
              onClick={() => {
                setLightboxIdx(null);
              }}
              className="rounded p-1 hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center gap-2 overflow-hidden px-2 pb-4">
            {photos.length > 1 ? (
              <button
                type="button"
                aria-label="Photo précédente"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) =>
                    i === null ? null : (i - 1 + photos.length) % photos.length,
                  );
                }}
                className="shrink-0 rounded-full p-2 text-white hover:bg-white/10"
              >
                <ChevronLeft className="h-7 w-7" />
              </button>
            ) : null}
            {current.url ? (
              <img
                src={current.url}
                alt={current.legende ?? ''}
                className="max-h-full max-w-full object-contain"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            ) : null}
            {photos.length > 1 ? (
              <button
                type="button"
                aria-label="Photo suivante"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) => (i === null ? null : (i + 1) % photos.length));
                }}
                className="shrink-0 rounded-full p-2 text-white hover:bg-white/10"
              >
                <ChevronRight className="h-7 w-7" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {pdfPreview ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/80">
          <div className="bg-card flex items-center justify-between gap-3 px-4 py-2">
            <span className="truncate text-sm font-medium">{pdfPreview.nom}</span>
            <div className="flex shrink-0 items-center gap-3">
              {pdfPreview.url ? (
                <a
                  href={pdfPreview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                >
                  <ExternalLink className="h-4 w-4" /> Onglet
                </a>
              ) : null}
              <button
                type="button"
                aria-label="Fermer l'aperçu"
                onClick={() => {
                  setPdfPreview(null);
                }}
                className="hover:bg-accent rounded p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="bg-muted flex-1">
            {pdfBlobUrl ? (
              <iframe title={pdfPreview.nom} src={pdfBlobUrl} className="h-full w-full border-0" />
            ) : (
              <div className="text-muted-foreground flex h-full items-center justify-center px-4 text-center text-sm">
                Chargement de l'aperçu… (s'il ne s'affiche pas, utilise « Onglet »)
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          disabled={!canSave || mutation.isPending}
          onClick={() => {
            mutation.mutate(form);
          }}
        >
          {mutation.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le chantier'}
        </Button>
        <Button asChild variant="outline">
          <Link to="/chantiers">Annuler</Link>
        </Button>
        {!canSave ? (
          <span className="text-muted-foreground text-sm">Le libellé est obligatoire.</span>
        ) : null}
      </div>

      {isEdit && isAdmin ? (
        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            className="border-destructive/40 text-destructive hover:bg-destructive/10"
            disabled={deleteMut.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Supprimer définitivement ce chantier ?\n\nLes photos et les heures pointées seront supprimées. Les devis et factures liés seront conservés (simplement détachés).',
                )
              ) {
                deleteMut.mutate();
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
            {deleteMut.isPending ? 'Suppression…' : 'Supprimer ce chantier'}
          </Button>
          <span className="text-muted-foreground text-xs">Réservé aux administrateurs.</span>
          {deleteMut.isError ? (
            <span className="text-destructive text-sm">
              {messageErreur(deleteMut.error)}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
